import { AUTH_PAGES, loginSchema, routes } from '@pmai/contracts';
import { Alert, Button, TextField } from '@pmai/ui';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { useApiMutation } from '../../shared/api/hooks';
import { safeNext } from '../../shared/auth/next';
import { useApplySession, useSession } from '../../shared/auth/session';
import { bind, useZodForm } from '../../shared/forms/form';
import { useErrorText } from '../../shared/i18n';
import { AuthCard } from './auth-layout';

/** FR-AUTH-002/003/104 */
export function LoginPage() {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const session = useSession();
  const applySession = useApplySession();
  const form = useZodForm(loginSchema, { defaultValues: { email: '', password: '' } });
  const login = useApiMutation(routes.auth.login, {
    onSuccess: (me) => {
      applySession(me);
      void navigate(next, { replace: true });
    },
  });

  if (session.data && !login.isPending) return <Navigate to={next} replace />;

  return (
    <AuthCard
      title={t('auth.login.title')}
      footer={
        <>
          <Link to={AUTH_PAGES.forgotPassword} className="text-brand-700 hover:underline">
            {t('auth.login.forgot')}
          </Link>
          <span>
            {t('auth.login.noAccount')}{' '}
            <Link to={AUTH_PAGES.register} className="text-brand-700 hover:underline">
              {t('auth.login.register')}
            </Link>
          </span>
        </>
      }
    >
      <form
        noValidate
        className="flex flex-col gap-4"
        onSubmit={form.handleSubmit((body) => login.mutate({ body }))}
      >
        {login.error && <Alert tone="danger">{errorText(login.error)}</Alert>}
        <TextField
          label={t('auth.email')}
          type="email"
          autoComplete="email"
          required
          {...bind(form, 'email')}
        />
        <TextField
          label={t('auth.password')}
          type="password"
          autoComplete="current-password"
          required
          {...bind(form, 'password')}
        />
        <Button type="submit" loading={login.isPending}>
          {t('auth.login.submit')}
        </Button>
      </form>
    </AuthCard>
  );
}
